import { useEffect, useRef } from 'react'

/**
 * Creates a hidden ruler element that can be used to measure a CSS length in pixels regardless of the current zoom
 * level or viewport size.
 *
 * This is useful for determining when a user has scrolled past a certain threshold defined in CSS units (e.g.,
 * "50vh", "10rem").
 *
 * @param rulerHeight the height of the ruler element as a CSS length expression
 */
const createRuler = (rulerHeight: string): HTMLDivElement => {
  const ruler = document.createElement('div')
  ruler.classList.add('scroll-trigger-ruler')
  ruler.style.position = 'absolute'
  ruler.style.visibility = 'hidden'
  ruler.style.pointerEvents = 'none'
  ruler.style.height = rulerHeight
  return ruler
}

/**
 * Executes a callback exactly once when the user scrolls past a specified CSS length.
 *
 * If the page does not have enough scrollable overflow to reach the threshold, the callback is executed immediately.
 *
 * @param {string} scrollThreshold - The scroll threshold that triggers the callback. This can be any valid CSS length (e.g., "200px", "50vh", "10rem").
 * @param {function} callback - The function to execute when the user scrolls past the specified length or if the page is too short to reach it.
 */
export const useScrollTrigger = (
  scrollThreshold: string,
  callback: () => void
) => {
  const hasTriggeredCallback = useRef(false)
  const scrollThresholdPx = useRef(0)

  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    // If we're in a non-browser environment or the callback was already triggered, do nothing.
    if (typeof window === 'undefined' || hasTriggeredCallback.current) {
      return () => {}
    }

    const cleanupFunctions: (() => void)[] = []

    const ruler = createRuler(scrollThreshold)
    document.body.appendChild(ruler)

    // Set initial value immediately before the observer fires its first tick
    scrollThresholdPx.current = ruler.getBoundingClientRect().height

    /**
     * Cleans up all resources and event listeners created by this hook.
     */
    const cleanup = () => {
      cleanupFunctions.forEach(cleanupFunction => cleanupFunction())
    }

    /**
     * Executes the callback and performs cleanup, ensuring that the callback is only executed once.
     */
    const triggerCallbackAndCleanup = (): void => {
      if (hasTriggeredCallback.current) {
        return
      }

      hasTriggeredCallback.current = true
      callbackRef.current()
      cleanup()
    }

    /**
     * Check whether the trigger condition is met and execute the callback if so.
     */
    const runCheck = (): void => {
      if (hasTriggeredCallback.current) {
        return
      }

      // Trigger the callback if the user has scrolled past the threshold
      if (window.scrollY >= scrollThresholdPx.current) {
        triggerCallbackAndCleanup()
        return
      }

      // Trigger the callback if the page is too short to scroll to the threshold
      const scrollableHeightPx =
        document.documentElement.scrollHeight - window.innerHeight
      if (scrollableHeightPx < scrollThresholdPx.current) {
        triggerCallbackAndCleanup()
      }
    }

    // Keep the threshold in sync with zooms/resizes
    const rulerObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        scrollThresholdPx.current = entry.contentRect.height
      })
      runCheck()
    })
    rulerObserver.observe(ruler)
    cleanupFunctions.push(() => {
      rulerObserver.disconnect()
      // Remove the ruler from the DOM when cleaning up to avoid memory leaks
      if (ruler.parentNode) {
        ruler.parentNode.removeChild(ruler)
      }
    })

    // Watch the document for height changes so we detect when the page becomes too short to scroll to the threshold
    const documentObserver = new ResizeObserver(() => runCheck())
    documentObserver.observe(document.documentElement)
    cleanupFunctions.push(() => {
      documentObserver.disconnect()
    })

    // Watch for scroll events to detect when the user has scrolled past the threshold
    const handleScroll = () => {
      runCheck()
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    cleanupFunctions.push(() =>
      window.removeEventListener('scroll', handleScroll)
    )

    // Check immediately in case the condition is already met
    runCheck()

    // Cleanup on component unmount
    return cleanup
  }, [scrollThreshold])
}
